import { useMemo, useState } from "react";
import { T, fontDisplay, fontMono } from "../theme";
import { fmtMoney } from "../engine/dates";
import { addRecurringCost, deleteRecurringCost, updateRecurringCost } from "../lib/data";

/**
 * Running costs — the standing overheads.
 *
 * Everything else in this tool mirrors a document, and is corrected by fixing
 * the document. These rows have nothing behind them but a judgement about how
 * the business runs, so they are edited here directly. That makes them the
 * easiest numbers in the forecast to get wrong and the most important ones to
 * be able to change, which is why every field is editable in place and every
 * row says where its figure came from.
 */

const CATEGORIES = [
  { id: "overheads", label: "Overheads" },
  { id: "staff", label: "Staff" },
  { id: "rent", label: "Rent" },
  { id: "software", label: "Software" },
  { id: "suppliers", label: "Suppliers" },
  { id: "expenses", label: "Expenses" },
  { id: "media", label: "Media spend" },
  { id: "debt", label: "Debt" },
  { id: "tax", label: "Tax" },
  { id: "other", label: "Other" },
];

const FREQUENCIES = [
  { id: "monthly", label: "a month", perMonth: 1 },
  { id: "weekly", label: "a week", perMonth: 52 / 12 },
  { id: "quarterly", label: "a quarter", perMonth: 1 / 3 },
  { id: "annual", label: "a year", perMonth: 1 / 12 },
];

const BLANK = () => ({
  payee: "",
  category: "other",
  amount: "",
  frequency: "monthly",
  month_rule: "day",
  day_of_month: 1,
  payment_method: "cash",
  is_variable: false,
  active: true,
  source_note: "",
});

const monthlyEquivalent = (r) =>
  (Number(r.amount) || 0) * (FREQUENCIES.find((f) => f.id === r.frequency)?.perMonth ?? 1);

export default function CostsView({ recurring = [], onChanged }) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(BLANK());
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [query, setQuery] = useState("");

  const rows = useMemo(
    () => [...recurring].sort((a, b) => monthlyEquivalent(b) - monthlyEquivalent(a)),
    [recurring]
  );

  // Thirty-odd lines is enough that finding one means scrolling past the rest.
  // Searchable by what it is called, what it is for, which group it sits in,
  // and what it costs — an amount on a statement is often the only thing to
  // hand when you are trying to work out what a payment was.
  const found = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.payee, r.source_note, r.overhead_group, r.category, r.payment_method, Math.round(Number(r.amount) || 0)]
        .filter((v) => v != null && v !== "")
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [rows, query]);

  // Grouped the way the business thinks about them, rather than by the
  // category that decides which line of the forecast they land on.
  const groups = useMemo(() => {
    const by = {};
    for (const r of found) {
      const g = r.overhead_group || "Ungrouped";
      (by[g] = by[g] || []).push(r);
    }
    return Object.entries(by)
      .map(([name, list]) => ({
        name,
        list,
        monthly: list.filter((r) => r.active).reduce((s, r) => s + monthlyEquivalent(r), 0),
      }))
      .sort((a, b) => b.monthly - a.monthly);
  }, [found]);

  const active = rows.filter((r) => r.active);
  const total = active.reduce((s, r) => s + monthlyEquivalent(r), 0);
  // What goes on the card matters separately: it does not leave the bank on
  // the day it is charged, it adds to a balance that has a limit and has to be
  // paid down later.
  const onCard = active
    .filter((r) => r.payment_method === "amex")
    .reduce((s, r) => s + monthlyEquivalent(r), 0);

  const run = async (key, fn) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
      await onChanged();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(null);
    }
  };

  const patch = (id, p) => run(id, () => updateRecurringCost(id, p));

  const cell = { padding: "7px 10px", fontSize: 12 };
  const th = { ...cell, textAlign: "left", fontSize: 11, color: T.muted, fontWeight: 500 };
  const field = (extra = {}) => ({
    fontFamily: "inherit",
    fontSize: 12,
    padding: "4px 6px",
    border: `1px solid ${T.border}`,
    borderRadius: 4,
    background: "transparent",
    ...extra,
  });

  return (
    <div>
      <h2 style={{ fontFamily: fontDisplay, fontSize: 22, marginBottom: 4 }}>Overheads</h2>
      <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.6, maxWidth: 720 }}>
        Everything that leaves the account whether or not anything is sold — payroll, rent,
        software, pensions, the small recurring things that are individually forgettable and
        collectively enormous. Nothing here is mirrored from a document, so these are judgements:
        change them, switch them off, or delete them outright.
      </p>
      <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.6, maxWidth: 720 }}>
        The forecast shows most of these as a single <strong>Overheads</strong> line rather than
        thirty-odd rows; the detail lives here. The larger commitments — payroll, rent, HMRC, the
        loan — keep their own lines, because they are worth seeing on their own.{" "}
        Anything marked <strong>AMEX</strong> is charged to the card rather than taken from the
        bank, so it does not appear on its own row in the forecast — it adds to the card balance on
        the day it is charged and leaves the bank later, inside the AMEX repayment. That is what
        makes the limit bite, and it is visible on the AMEX tab.
      </p>

      <div style={{ display: "flex", gap: 12, margin: "14px 0", alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ background: T.panel, borderRadius: 10, padding: "12px 16px" }}>
          <div style={{ fontSize: 10, textTransform: "uppercase", color: T.muted, letterSpacing: 0.8 }}>
            In the forecast
          </div>
          <div style={{ fontFamily: fontDisplay, fontSize: 24 }}>{fmtMoney(total)}</div>
          <div style={{ fontSize: 11, color: T.muted }}>
            a month · {active.length} costs
          </div>
        </div>
        {onCard > 0 && (
          <div style={{ background: T.panel, borderRadius: 10, padding: "12px 16px" }}>
            <div style={{ fontSize: 10, textTransform: "uppercase", color: T.muted, letterSpacing: 0.8 }}>
              On the AMEX
            </div>
            <div style={{ fontFamily: fontDisplay, fontSize: 24, color: T.accent }}>
              {fmtMoney(onCard)}
            </div>
            <div style={{ fontSize: 11, color: T.muted }}>
              a month · reaches the bank when the statement is paid
            </div>
          </div>
        )}
        <button
          onClick={() => {
            setDraft(BLANK());
            setAdding((v) => !v);
          }}
          style={{
            padding: "8px 14px",
            borderRadius: 6,
            border: "none",
            background: T.accent,
            color: "#fff",
            fontSize: 12,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          {adding ? "Cancel" : "Add a cost"}
        </button>
      </div>

      {error && <div style={{ color: T.cost, fontSize: 12, marginBottom: 8 }}>{error}</div>}

      {adding && (
        <div style={{ background: T.panel, borderRadius: 10, padding: 14, marginBottom: 14 }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
            <label style={{ fontSize: 11, color: T.muted }}>
              What
              <br />
              <input
                value={draft.payee}
                onChange={(e) => setDraft({ ...draft, payee: e.target.value })}
                placeholder="Rent"
                style={field({ width: 200, marginTop: 2 })}
              />
            </label>
            <label style={{ fontSize: 11, color: T.muted }}>
              Amount
              <br />
              <input
                value={draft.amount}
                onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
                placeholder="5000"
                style={field({ width: 100, marginTop: 2, fontFamily: fontMono })}
              />
            </label>
            <label style={{ fontSize: 11, color: T.muted }}>
              Every
              <br />
              <select
                value={draft.frequency}
                onChange={(e) => setDraft({ ...draft, frequency: e.target.value })}
                style={field({ marginTop: 2 })}
              >
                {FREQUENCIES.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.label}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ fontSize: 11, color: T.muted }}>
              Category
              <br />
              <select
                value={draft.category}
                onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                style={field({ marginTop: 2 })}
              >
                {CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ fontSize: 11, color: T.muted }}>
              Paid by
              <br />
              <select
                value={draft.payment_method}
                onChange={(e) => setDraft({ ...draft, payment_method: e.target.value })}
                style={field({ marginTop: 2 })}
              >
                <option value="cash">Bank</option>
                <option value="amex">AMEX</option>
              </select>
            </label>
            {draft.frequency === "monthly" && (
              <label style={{ fontSize: 11, color: T.muted }}>
                Paid
                <br />
                <select
                  value={draft.month_rule}
                  onChange={(e) => setDraft({ ...draft, month_rule: e.target.value })}
                  style={field({ marginTop: 2 })}
                >
                  <option value="day">on a set date</option>
                  <option value="last_friday">the last Friday</option>
                  <option value="spread">spread evenly</option>
                </select>
              </label>
            )}
            {draft.frequency === "monthly" && draft.month_rule === "day" && (
              <label style={{ fontSize: 11, color: T.muted }}>
                on the
                <br />
                <input
                  type="number"
                  min="1"
                  max="28"
                  value={draft.day_of_month}
                  onChange={(e) => setDraft({ ...draft, day_of_month: e.target.value })}
                  style={field({ width: 54, marginTop: 2, fontFamily: fontMono })}
                />
              </label>
            )}
            <button
              disabled={busy === "new" || !draft.payee.trim() || !Number(draft.amount)}
              onClick={() =>
                run("new", async () => {
                  await addRecurringCost({
                    ...draft,
                    amount: Number(draft.amount),
                    day_of_month: Math.min(Math.max(Number(draft.day_of_month) || 1, 1), 28),
                    source_note: draft.source_note || "Added by hand.",
                  });
                  setAdding(false);
                  setDraft(BLANK());
                })
              }
              style={{
                marginLeft: "auto",
                padding: "7px 14px",
                borderRadius: 6,
                border: "none",
                background: draft.payee.trim() && Number(draft.amount) ? T.accent : T.border,
                color: draft.payee.trim() && Number(draft.amount) ? "#fff" : T.muted,
                fontSize: 12,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              {busy === "new" ? "Adding…" : "Add"}
            </button>
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Name, group, category, description, amount…"
          style={{
            fontFamily: "inherit",
            fontSize: 12,
            padding: "7px 10px",
            width: 300,
            borderRadius: 6,
            border: `1px solid ${T.border}`,
          }}
        />
        {query && (
          <>
            <button
              onClick={() => setQuery("")}
              style={{ border: "none", background: "none", color: T.muted, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}
            >
              clear
            </button>
            <span style={{ fontSize: 11, color: T.muted }}>
              {found.length} of {rows.length} · {fmtMoney(found.filter((r) => r.active).reduce((s2, r) => s2 + monthlyEquivalent(r), 0))} a month
            </span>
          </>
        )}
      </div>

      <div style={{ background: T.panel, borderRadius: 10, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...th, width: 34 }}>On</th>
              <th style={th}>What</th>
              <th style={{ ...th, textAlign: "right" }}>Amount</th>
              <th style={th}>Every</th>
              <th style={th}>Paid</th>
              <th style={th}>Paid by</th>
              <th style={th}>Category</th>
              <th style={{ ...th, textAlign: "right" }}>Per month</th>
              <th style={{ ...th, width: 60 }} />
            </tr>
          </thead>
          <tbody>
            {query && !found.length && (
              <tr>
                <td colSpan={9} style={{ ...cell, color: T.muted }}>
                  Nothing matches “{query}”.
                </td>
              </tr>
            )}
            {groups.flatMap((g) => [
              <tr key={`g-${g.name}`}>
                <td
                  colSpan={9}
                  style={{
                    ...cell,
                    background: T.panelSoft,
                    fontWeight: 700,
                    fontSize: 11,
                    borderTop: `1px solid ${T.border}`,
                  }}
                >
                  {g.name}
                  <span style={{ fontWeight: 400, color: T.muted, marginLeft: 8 }}>
                    {fmtMoney(g.monthly)} a month · {g.list.length} line
                    {g.list.length === 1 ? "" : "s"}
                  </span>
                </td>
              </tr>,
              ...g.list.map((r) => (
              <tr
                key={r.id}
                style={{
                  borderTop: `1px solid ${T.borderSoft}`,
                  opacity: r.active ? 1 : 0.5,
                  background: confirmDelete === r.id ? "#FBEFE2" : undefined,
                }}
              >
                <td style={cell}>
                  <input
                    type="checkbox"
                    checked={!!r.active}
                    disabled={busy === r.id}
                    title="Include in the forecast"
                    onChange={(e) => patch(r.id, { active: e.target.checked })}
                  />
                </td>
                <td style={cell}>
                  <input
                    key={`p-${r.id}-${r.payee}`}
                    defaultValue={r.payee}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v && v !== r.payee) patch(r.id, { payee: v });
                    }}
                    style={field({ width: 210 })}
                  />
                  {/* Where a figure came from is the thing most likely to need
                      correcting later, so it is editable rather than a fact
                      frozen at the moment of import. */}
                  {/* One line when empty, so a row without a note stays a row
                      rather than a box waiting to be filled. */}
                  <textarea
                    key={`n-${r.id}-${r.source_note || ""}`}
                    defaultValue={r.source_note || ""}
                    placeholder="add a note…"
                    rows={r.source_note ? 2 : 1}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v !== (r.source_note || "")) patch(r.id, { source_note: v || null });
                    }}
                    style={{
                      display: "block",
                      marginTop: 3,
                      width: r.source_note ? 320 : 150,
                      fontFamily: "inherit",
                      fontSize: 10,
                      lineHeight: 1.45,
                      color: T.muted,
                      padding: "4px 6px",
                      border: `1px solid ${T.borderSoft}`,
                      borderRadius: 4,
                      background: "transparent",
                      resize: "vertical",
                    }}
                  />
                </td>
                <td style={{ ...cell, textAlign: "right" }}>
                  <input
                    key={`a-${r.id}-${r.amount}`}
                    defaultValue={r.amount}
                    onBlur={(e) => {
                      const v = Number(e.target.value);
                      if (v && v !== Number(r.amount)) patch(r.id, { amount: v });
                    }}
                    style={field({ width: 92, textAlign: "right", fontFamily: fontMono })}
                  />
                </td>
                <td style={cell}>
                  <select
                    value={r.frequency}
                    onChange={(e) => patch(r.id, { frequency: e.target.value })}
                    style={field()}
                  >
                    {FREQUENCIES.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={cell}>
                  {r.frequency === "monthly" ? (
                    <>
                      <select
                        value={r.month_rule || "day"}
                        onChange={(e) => patch(r.id, { month_rule: e.target.value })}
                        style={field()}
                      >
                        <option value="day">on the</option>
                        <option value="last_friday">last Friday</option>
                        <option value="spread">spread evenly</option>
                      </select>
                      {(r.month_rule || "day") === "day" && (
                        <input
                          type="number"
                          min="1"
                          max="28"
                          key={`d-${r.id}-${r.day_of_month}`}
                          defaultValue={r.day_of_month || 1}
                          onBlur={(e) => {
                            const v = Math.min(Math.max(Number(e.target.value) || 1, 1), 28);
                            if (v !== r.day_of_month) patch(r.id, { day_of_month: v });
                          }}
                          style={field({ width: 46, marginLeft: 4, fontFamily: fontMono })}
                        />
                      )}
                    </>
                  ) : (
                    <span style={{ fontSize: 11, color: T.muted }}>—</span>
                  )}
                </td>
                <td style={cell}>
                  <select
                    value={r.payment_method || "cash"}
                    onChange={(e) => patch(r.id, { payment_method: e.target.value })}
                    style={field(
                      r.payment_method === "amex"
                        ? { borderColor: T.accent, background: T.accentSoft }
                        : {}
                    )}
                  >
                    <option value="cash">Bank</option>
                    <option value="amex">AMEX</option>
                  </select>
                </td>
                <td style={cell}>
                  <select
                    value={r.category || "other"}
                    onChange={(e) => patch(r.id, { category: e.target.value })}
                    style={field()}
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={{ ...cell, textAlign: "right", fontFamily: fontMono }}>
                  {fmtMoney(monthlyEquivalent(r))}
                </td>
                <td style={{ ...cell, textAlign: "right" }}>
                  {confirmDelete === r.id ? (
                    <>
                      <button
                        onClick={() =>
                          run(r.id, async () => {
                            await deleteRecurringCost(r.id);
                            setConfirmDelete(null);
                          })
                        }
                        style={{
                          border: "none",
                          background: T.cost,
                          color: "#fff",
                          borderRadius: 4,
                          padding: "3px 8px",
                          fontSize: 11,
                          cursor: "pointer",
                          fontFamily: "inherit",
                        }}
                      >
                        Delete
                      </button>
                      <button
                        onClick={() => setConfirmDelete(null)}
                        style={{
                          border: "none",
                          background: "transparent",
                          color: T.muted,
                          fontSize: 11,
                          cursor: "pointer",
                          marginLeft: 4,
                          fontFamily: "inherit",
                        }}
                      >
                        no
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setConfirmDelete(r.id)}
                      title="Delete this cost"
                      style={{
                        border: "none",
                        background: "transparent",
                        color: T.muted,
                        fontSize: 14,
                        cursor: "pointer",
                        lineHeight: 1,
                      }}
                    >
                      ×
                    </button>
                  )}
                </td>
              </tr>
              )),
            ])}
          </tbody>
        </table>
        {!rows.length && (
          <div style={{ fontSize: 12, color: T.muted, padding: 14 }}>
            No running costs yet. Without them the forecast shows only invoices and bills, which run
            out after a few weeks and make the months beyond look far healthier than they are.
          </div>
        )}
      </div>
    </div>
  );
}
